import { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, TrendingUp, AlertTriangle, Lightbulb } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  type?: 'anomaly' | 'insight' | 'scenario' | 'normal';
}

interface ForecastAIAssistantProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ForecastAIAssistant({ isOpen, onClose }: ForecastAIAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: "Hi! I'm your AI forecasting assistant. I can help you with:\n\n• **Scenario Planning** - Test different volume scenarios\n• **Anomaly Detection** - Identify unusual patterns in your data\n• **Insights & Recommendations** - Get tips to improve occupancy and reduce shrinkage\n\nWhat would you like to explore?",
      timestamp: new Date(),
      type: 'normal',
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Demo AI responses based on keywords
  const generateAIResponse = (userMessage: string): Message => {
    const lowerMessage = userMessage.toLowerCase();

    // Anomaly detection
    if (lowerMessage.includes('anomaly') || lowerMessage.includes('spike') || lowerMessage.includes('unusual')) {
      return {
        id: Date.now().toString(),
        role: 'assistant',
        content: "🔍 **Anomaly Detected**\n\nI've identified an unusual spike on **Tuesday at 2:00 PM** - volume was 245% higher than the 4-week average.\n\n**Analysis:**\n• Historical avg: 42 contacts/15min\n• Tuesday spike: 145 contacts/15min\n• Likely cause: Marketing campaign or product launch\n\n**Recommendation:**\nWould you like me to:\n1. Exclude this outlier from future predictions?\n2. Include it as a recurring pattern?\n3. Create a separate forecast for campaign days?",
        timestamp: new Date(),
        type: 'anomaly',
      };
    }

    // Scenario planning
    if (lowerMessage.includes('scenario') || lowerMessage.includes('what if') || lowerMessage.includes('increase') || lowerMessage.includes('decrease')) {
      return {
        id: Date.now().toString(),
        role: 'assistant',
        content: "📊 **Scenario Planning Results**\n\nLet's model a +20% volume increase across all channels:\n\n**Current State:**\n• Avg daily volume: 3,450 contacts\n• Staff required: 38 FTE\n• Occupancy: 85%\n\n**With +20% Volume:**\n• New daily volume: 4,140 contacts (+690)\n• Staff required: 46 FTE (+8)\n• Recommended occupancy: 82% (to maintain SLA)\n\n**Cost Impact:**\n• Additional monthly cost: ~$32,000\n• Cost per contact: Decreases from $4.20 → $3.95\n\nWould you like me to generate a detailed staffing plan for this scenario?",
        timestamp: new Date(),
        type: 'scenario',
      };
    }

    // Shrinkage insights
    if (lowerMessage.includes('shrinkage') || lowerMessage.includes('occupancy') || lowerMessage.includes('improve')) {
      return {
        id: Date.now().toString(),
        role: 'assistant',
        content: "💡 **Shrinkage & Occupancy Insights**\n\n**Current Metrics:**\n• Overall shrinkage: 22.5%\n• Target shrinkage: 20%\n• Occupancy: 85%\n\n**Weekly Patterns Detected:**\n• Monday: 28% shrinkage (above average)\n  - Reason: Weekend backlog catch-up\n• Friday: 24% shrinkage (slightly high)\n  - Reason: End-of-week training sessions\n\n**Recommendations:**\n1. **Shift training to Tuesday/Wednesday** - Could reduce Friday shrinkage to 20% (-4%)\n2. **Add 2 agents on Monday mornings** - Reduce backlog impact\n3. **Optimize break scheduling** - Current breaks cluster at :30 and :00, consider staggering\n\n**Potential Impact:**\n• Reduce overall shrinkage from 22.5% → 19.5%\n• Save ~$15,000/month in staffing costs\n• Improve Monday morning SLA from 78% → 88%",
        timestamp: new Date(),
        type: 'insight',
      };
    }

    // Day of week patterns
    if (lowerMessage.includes('monday') || lowerMessage.includes('tuesday') || lowerMessage.includes('day')) {
      return {
        id: Date.now().toString(),
        role: 'assistant',
        content: "📅 **Weekly Pattern Analysis**\n\n**Volume by Day of Week:**\n• Monday: 125% of average (highest)\n• Tuesday: 110% of average\n• Wednesday: 95% of average\n• Thursday: 90% of average\n• Friday: 80% of average (lowest)\n\n**Insights:**\n• Monday spike driven by weekend inquiries\n• Friday dip suggests customers prefer early-week contact\n• Recommend shifting 3-4 FTE from Friday → Monday\n\n**Shrinkage Patterns:**\n• Monday: Higher unplanned absences (28%)\n• Wednesday: Lowest shrinkage (18%) - best day for training\n• Friday: More planned time off (24%)\n\nWould you like me to generate an optimized weekly staffing schedule based on these patterns?",
        timestamp: new Date(),
        type: 'insight',
      };
    }

    // Default helpful response
    return {
      id: Date.now().toString(),
      role: 'assistant',
      content: "I can help you with:\n\n**Anomaly Detection:**\n• \"Show me any anomalies in this week's data\"\n• \"Why was there a spike on Tuesday?\"\n\n**Scenario Planning:**\n• \"What if volume increases by 20%?\"\n• \"Model a scenario with 5 fewer agents\"\n\n**Insights:**\n• \"How can I reduce shrinkage?\"\n• \"What patterns do you see by day of week?\"\n• \"Give me tips to improve occupancy\"\n\nTry asking me one of these questions!",
      timestamp: new Date(),
      type: 'normal',
    };
  };

  const handleSend = () => {
    if (!input.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate AI thinking delay
    setTimeout(() => {
      const aiResponse = generateAIResponse(input);
      setMessages((prev) => [...prev, aiResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getMessageIcon = (type?: string) => {
    switch (type) {
      case 'anomaly':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'insight':
        return <Lightbulb className="h-4 w-4 text-yellow-600" />;
      case 'scenario':
        return <TrendingUp className="h-4 w-4 text-blue-600" />;
      default:
        return <Sparkles className="h-4 w-4 text-purple-600" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[700px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">AI Forecast Assistant</h2>
              <p className="text-sm text-gray-600">Scenario planning, anomaly detection & insights</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Quick Actions */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setInput("Show me any anomalies in this week's data")}
              className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full hover:bg-orange-100 transition-colors"
            >
              🔍 Detect Anomalies
            </button>
            <button
              onClick={() => setInput("What if volume increases by 20%?")}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
            >
              📊 Run Scenario
            </button>
            <button
              onClick={() => setInput("How can I reduce shrinkage?")}
              className="px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-full hover:bg-yellow-100 transition-colors"
            >
              💡 Get Insights
            </button>
            <button
              onClick={() => setInput("What patterns do you see by day of week?")}
              className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full hover:bg-green-100 transition-colors"
            >
              📅 Weekly Patterns
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-4 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="flex items-center space-x-2 mb-2">
                    {getMessageIcon(message.type)}
                    <span className="text-xs font-semibold text-gray-600">AI Assistant</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {message.content}
                </div>
                <div className="text-xs opacity-70 mt-2">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg p-4 max-w-[80%]">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-xs text-gray-600">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex space-x-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about scenarios, anomalies, or get insights..."
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isTyping}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <Send className="h-4 w-4" />
              <span>Send</span>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Press Enter to send • Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}
